import { Box, Typography, CircularProgress } from '@mui/material';
import { getI18n } from 'client/AppReducer';
import App from 'client/modules/App/App';
import {
  helpSlice,
  useListCategoriesQuery,
  useReadContentQuery,
} from 'client/modules/Help/helpSlice';
import Layout from 'components/Layout/LayoutContainer';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SearchBox from './SearchBox';
import { findCategoryByUrlSlug } from './helpSlugUtils';
import {
  helpContentHomeSx,
  helpContentSx,
  helpHeaderSectionSx,
  helpHomePageLayoutSx,
  helpLeftNavSx,
  helpMainSx,
  helpSearchContainerSx,
  helpSearchResultExcerptSx,
  helpSearchResultItemSx,
  helpSearchResultsSx,
  helpSearchResultsTitleSx,
  helpSearchResultTitleSx,
  helpSearchBoxContainerHomeSx,
  helpTitleSx,
} from './helpSsrStyles';

function HelpLayout({ store }) {
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const { category, article } = useParams();
  const { formatMessage, locale } = useIntl();
  const { data: categories } = useListCategoriesQuery(locale);
  const { data: articleObj } = useReadContentQuery(
    { article, category, locale },
    { skip: !article },
  );
  const categoryObj = findCategoryByUrlSlug(categories, category) || articleObj?.category;
  const categoryName = categoryObj?.name;
  const categorySlug = categoryObj?.slug || category;
  const title = `Promoter Ninja - ${
    articleObj?.title ||
    categoryName ||
    formatMessage({ id: 'help.home', defaultMessage: 'Help Center' })
  }`;
  const description = articleObj?.description || categoryObj?.description;

  const isHomePage = !category && !article;
  return (
    <Layout
      rootSx={isHomePage ? helpHomePageLayoutSx : undefined}
      transparent={isHomePage}
      reverse={isHomePage}
      disableGutters
      leftNav={!isHomePage ? <SearchBox locale={locale} setSearching={setIsSearching} /> : null}
      backContent={
        category ? (
          <Box sx={helpLeftNavSx}>
            <ul>
              <li>
                <Link to="/help">
                  <FormattedMessage id="help.home" defaultMessage="Help Center" />
                </Link>
              </li>
              {categoryName ? (
                <li>
                  {article ? (
                    <Link to={`/help/${categorySlug}`}>{categoryName}</Link>
                  ) : (
                    categoryName
                  )}
                </li>
              ) : null}
              {article && articleObj ? <li>{articleObj.title}</li> : null}
            </ul>
          </Box>
        ) : null
      }
    >
      <Box sx={helpMainSx}>
        {isHomePage && (
          <Box sx={helpHeaderSectionSx}>
            <Typography variant="h4" sx={helpTitleSx}>
              <FormattedMessage
                id="help.home.title"
                defaultMessage="👋 Hi, how can we help you today?"
              />
            </Typography>
            <Box sx={helpSearchContainerSx}>
              <SearchBox
                locale={locale}
                setSearchResults={setSearchResults}
                setSearching={setIsSearching}
                containerSx={helpSearchBoxContainerHomeSx}
                size="medium"
              />
            </Box>
          </Box>
        )}

        {isHomePage && (isSearching || searchResults?.results) && (
          <Box sx={helpSearchResultsSx}>
            {isSearching ? (
              <Typography variant="h6" sx={helpSearchResultsTitleSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <FormattedMessage id="help.search.searching" defaultMessage="Searching..." />
                </Box>
              </Typography>
            ) : (
              <Typography variant="h6" sx={helpSearchResultsTitleSx}>
                <FormattedMessage
                  id="help.search.results"
                  defaultMessage="Search Results ({count})"
                  values={{ count: searchResults.results.length }}
                />
              </Typography>
            )}
            {!isSearching &&
              searchResults?.results &&
              searchResults.results.map(result => (
                <Box key={result.slug} sx={helpSearchResultItemSx}>
                  <Link
                    to={`/help/${result.categorySlug}/${result.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <Typography variant="h6" sx={helpSearchResultTitleSx}>
                      {result.title || result.slug}
                    </Typography>
                    {result.highlightedExcerpt && (
                      <Typography
                        variant="body2"
                        sx={helpSearchResultExcerptSx}
                        dangerouslySetInnerHTML={{
                          __html: result.highlightedExcerpt,
                        }}
                      />
                    )}
                  </Link>
                </Box>
              ))}
          </Box>
        )}
        <Box sx={isHomePage ? helpContentHomeSx : helpContentSx}>
          <App store={store} />
        </Box>
        <Helmet>
          <title>{title}</title>
          {!description || <meta name="description" content={description} />}
        </Helmet>
      </Box>
    </Layout>
  );
}

HelpLayout.propTypes = {
  store: PropTypes.object.isRequired,
};

HelpLayout.loader =
  ({ dispatch, getState }) =>
  ({ params: { category, article } }) => {
    const { locale } = getI18n(getState());
    dispatch(helpSlice.endpoints.listCategories.initiate(locale));
    if (category) {
      dispatch(helpSlice.endpoints.listContents.initiate({ category, locale }));
    }
    if (article) {
      dispatch(helpSlice.endpoints.readContent.initiate({ article, category, locale }));
    }
    dispatch(helpSlice.endpoints.readContent.initiate(locale));
    return Promise.all(dispatch(helpSlice.util.getRunningQueriesThunk()));
  };

export default HelpLayout;
